import React, { useMemo, useState } from "react";
import { Box, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import { Image as ImageIcon } from "@mui/icons-material";
import {
  isAllowedCarouselHref,
  MAP_CAROUSEL_LOGO_DISPLAY_MAX_HEIGHT_PX,
  MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX,
} from "../../services/mapCarousel";

const MAILTO_PREFIX = /^mailto:/i;

function isLogoPreviewableUrl(href) {
  if (typeof href !== "string") return false;
  const t = href.trim();
  if (!t || !isAllowedCarouselHref(t)) return false;
  if (MAILTO_PREFIX.test(t)) return false;
  return true;
}

function resolveLogoPreviewSrc(href) {
  const t = typeof href === "string" ? href.trim() : "";
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("/") && !t.startsWith("//")) {
    return `${typeof window !== "undefined" ? window.location.origin : ""}${t}`;
  }
  return t;
}

/**
 * Popover with a dashed outline matching the CareMap carousel logo slot; image uses the same
 * max dimensions and object-fit as {@link AdCarousel} so the preview matches the live map.
 */
export function CarouselLogoSlotPreviewButton({ logoUrl, onInvalidUrl, ariaLabel }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const trimmed = typeof logoUrl === "string" ? logoUrl.trim() : "";
  const previewable = isLogoPreviewableUrl(trimmed);
  const open = Boolean(anchorEl);
  const imgSrc = useMemo(() => (previewable ? resolveLogoPreviewSrc(trimmed) : ""), [
    previewable,
    trimmed,
  ]);

  const handleOpen = (event) => {
    if (!trimmed) {
      return;
    }
    if (!previewable) {
      onInvalidUrl?.();
      return;
    }
    setLoadError(false);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip
        title={
          previewable
            ? "Preview logo inside the CareMap carousel slot (dashed outline)"
            : trimmed
              ? "Use https:// or an in-app path (not mailto:) to preview"
              : "Enter a logo URL to preview"
        }
      >
        <span>
          <IconButton
            edge="end"
            aria-label={ariaLabel}
            onClick={handleOpen}
            disabled={!trimmed}
            size="small"
          >
            <ImageIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 2,
            maxWidth: 340,
            overflow: "visible",
          },
        }}
        sx={{ overflow: "visible" }}
      >
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Dashed outline matches the map logo slot (~{MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX}×
          {MAP_CAROUSEL_LOGO_DISPLAY_MAX_HEIGHT_PX}px).
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          As on CareMap
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", overflow: "visible", py: 0.5, mb: 2 }}>
          <Box
            sx={{
              width: MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX,
              height: MAP_CAROUSEL_LOGO_DISPLAY_MAX_HEIGHT_PX,
              border: (theme) => `2px dashed ${theme.palette.text.secondary}`,
              borderRadius: 1,
              boxSizing: "border-box",
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {loadError ? (
              <Typography variant="caption" color="error" align="center" sx={{ px: 0.5 }}>
                Could not load image. Try a public https URL or check the path.
              </Typography>
            ) : (
              <Box
                component="img"
                src={imgSrc}
                alt=""
                onError={() => setLoadError(true)}
                sx={{
                  maxWidth: `${MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX}px`,
                  maxHeight: `${MAP_CAROUSEL_LOGO_DISPLAY_MAX_HEIGHT_PX}px`,
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        </Box>

        {!loadError && imgSrc ? (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Same image at slot width (scroll if tall) — dashed band is the map’s height; anything
              sticking out is scaled down on CareMap to fit inside.
            </Typography>
            <Box
              sx={{
                position: "relative",
                width: MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX,
                maxHeight: 160,
                overflowY: "auto",
                mx: "auto",
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Box
                component="img"
                src={imgSrc}
                alt=""
                sx={{
                  width: MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX,
                  height: "auto",
                  display: "block",
                }}
              />
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX,
                  height: MAP_CAROUSEL_LOGO_DISPLAY_MAX_HEIGHT_PX,
                  border: (theme) => `2px dashed ${theme.palette.primary.main}`,
                  borderRadius: 1,
                  boxSizing: "border-box",
                  pointerEvents: "none",
                }}
              />
            </Box>
          </>
        ) : null}
      </Popover>
    </>
  );
}
